import React from 'react';
import { Link } from 'react-router-dom';
import { ExportOutlined } from '@ant-design/icons';
import { Col, Input, Row, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ReconciliationMismatch } from '@cbinsights/salesforceservice/salesforceservice';
import { useGetReconciliationMismatchesQuery } from '../../services/hooks/useGetReconciliationMismatchesQuery';
import useFuseSearch from '../../hooks/useFuseSearch';
import useShowPagination from '../../hooks/useShowPagination';

const SF_BASE_URL = 'https://cbinsights.lightning.force.com/lightning/r';

const REASON_DESCRIPTIONS: Record<string, string> = {
  NO_TEAMS_FOUND: 'No teams found for account ID',
  EXPIRED_PACKAGE: 'Team is on expired package',
  SUBSCRIPTION_DATE_MISMATCH:
    'Subscription dates do not match team package dates',
  NO_ACTIVE_PAID_USERS: 'Team has no active paid users',
};

const columns: ColumnsType<ReconciliationMismatch> = [
  {
    title: 'Opportunity ID',
    dataIndex: 'opportunityId',
    key: 'opportunityId',
    render: (id: string) => (
      <a
        href={`${SF_BASE_URL}/Opportunity/${id}/view`}
        target="_blank"
        rel="noreferrer"
        className="text-blue-500 underline"
      >
        {id} <ExportOutlined />
      </a>
    ),
  },
  {
    title: 'Account ID',
    dataIndex: 'accountId',
    key: 'accountId',
    render: (id: string) => (
      <a
        href={`${SF_BASE_URL}/Account/${id}/view`}
        target="_blank"
        rel="noreferrer"
        className="text-blue-500 underline"
      >
        {id} <ExportOutlined />
      </a>
    ),
  },
  {
    title: 'Team ID',
    dataIndex: 'teamId',
    key: 'teamId',
    render: (id: number) => (
      <Link to={`/teams/${id}`} className="text-blue-500 underline">
        {id}
      </Link>
    ),
  },
  {
    title: 'Mismatch Classification',
    dataIndex: 'reason',
    key: 'reason',
    render: (reason: string) => REASON_DESCRIPTIONS[reason] ?? reason,
    filters: Object.keys(REASON_DESCRIPTIONS).map((key) => ({
      text: key,
      value: key,
    })),
    onFilter: (value, record) => record.reason === value,
  },
];

const ReconciliationMismatchesPage = () => {
  const { data: reconciliationMismatches } =
    useGetReconciliationMismatchesQuery({ enabled: true, params: {} });

  const { setSearchQuery, results: filteredData } = useFuseSearch({
    data: reconciliationMismatches?.mismatches || [],
    options: {
      keys: ['opportunityId', 'accountId', 'teamId'],
      shouldSort: true,
      threshold: 0.3,
    },
  });

  const showPagination = useShowPagination({
    data: filteredData,
    config: { pageSize: 25 },
  });

  return (
    <Row justify="center" className="mt-8">
      <Col xs={{ span: 22 }} md={{ span: 18 }} lg={{ span: 16 }}>
        <Typography.Title level={3}>
          Salesforce Reconciliation Mismatches
        </Typography.Title>
        <Input.Search
          placeholder="Search by Opportunity ID, Account ID, or Team ID"
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-4"
          allowClear
        />
        <Table
          bordered
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) =>
            `${record.opportunityId}-${record.accountId}-${record.teamId}-${record.packageId}-${record.reason}`
          }
          locale={{ emptyText: 'No recent reconciliation issues.' }}
          pagination={showPagination}
        />
      </Col>
    </Row>
  );
};

export default ReconciliationMismatchesPage;
